import React, { useEffect, useMemo } from 'react';

import { css, Global } from '@emotion/react';
import { Flex } from '@opendoor/bricks/core';
import { useLocation } from '@remix-run/react';
import { useMachine } from '@xstate/react';

import { useQuery } from 'platform/apollo';
import { FullScreenLoader } from 'platform/components/FullScreenLoader';
import navigationHelpers from 'platform/navigationHelpers';
import { PageState, useAutoTrackPageViews } from 'platform/useAutoTrackPageViews';

import { HOMEPAGE_URL } from 'shared/helpers/globals';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

import { DownloadApp, PropertyConfirmation } from 'buying/components/showingRequestCreation';
import { Header } from 'buying/components/showingRequestCreation/Header';
import {
  ShowingRequestPropertyQuery,
  ShowingRequestPropertyQueryVariables,
} from 'buying/graphql/queries/__generated__/ShowingRequestPropertyQuery';
import { ShowingRequestProperty } from 'buying/graphql/queries/ShowingRequestProperty';
import { ShowingRequestCreationStateMachine } from 'buying/stateMachines/showingRequestCreationStateMachine';

const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';

/**
 * Showing request creation flow
 */
export const ShowingRequestCreation: React.FC = () => {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const addressId = queryParams.get('id');
  const { data, loading, error } = useQuery<
    ShowingRequestPropertyQuery,
    ShowingRequestPropertyQueryVariables
  >(ShowingRequestProperty, {
    variables: {
      addressId: addressId || '',
    },
    skip: !addressId,
  });
  const property = data?.buyer?.getPropertyByAddressId?.property;
  const propertyPhoto = property?.photos?.[0]?.urls?.size768x512;

  const [state, send] = useMachine(ShowingRequestCreationStateMachine);
  const { value } = state;

  useAutoTrackPageViews(loading ? PageState.LOADING : PageState.LOADED);

  useEffect(() => {
    if (value === 'IDLE' && property) {
      send({ type: 'setAddressId', value: addressId });
      send({ type: 'setProperty', value: property });
    }
  }, [addressId, property, value, send]);

  useEffect(() => {
    if (value === 'IDLE' && !loading) {
      send('FORWARD');
    }
  }, [value, loading, send]);

  const handleOnNext = () => {
    send('FORWARD');
  };

  /**
   * For terminal states, redirect to PDP
   */
  if (state.value === 'REDIRECT') {
    navigationHelpers.hardNavAssign(
      `${HOMEPAGE_URL}/properties/aid_${state.context.property?.addressId}`,
    );
  } else if (state.value === 'APP_REDIRECT') {
    navigationHelpers.hardNavAssign(APP_DOWNLOAD_URL);
  }

  let activeStep = null;
  switch (state.value) {
    case 'PROPERTY_CONFIRM':
      activeStep = <PropertyConfirmation handleOnNext={handleOnNext} property={property} />;
      break;
    case 'DOWNLOAD_APP':
      activeStep = <DownloadApp handleOnNext={handleOnNext} />;
      break;
    default:
      activeStep = null;
      break;
  }

  if (!addressId || error) {
    return <NotFoundPage />;
  }

  if (loading || value === 'REDIRECT' || value === 'APP_REDIRECT') {
    return <FullScreenLoader from="showing-request-creation" reason="loading" />;
  }

  const showHeader = value !== 'PROPERTY_CONFIRM';

  return (
    <>
      <Flex
        alignItems="center"
        flexDirection="column"
        maxWidth={['100%', null, 'xl']}
        px={[null, null, 8]}
        pt={0}
        m={5}
      >
        <Header
          bedrooms={property?.bedrooms}
          bathrooms={property?.bathroomsTotal}
          sqft={property?.totalLivingSqFt}
          photo={propertyPhoto}
          show={showHeader}
          street={property?.street}
        />
        <Flex pt={5} mx={[5, null, 5]} justifyContent="center">
          {activeStep}
        </Flex>
      </Flex>
      <Global
        styles={css`
          body {
            background-color: #fff;
            height: auto;
          }
        `}
      />
    </>
  );
};
